<template>
  <div class="loyalty text-neutral-800">
    <div class="loyalty-header bg-neutral-50">
      <div class="my-10 md:my-0 md:py-10 md:flex md:flex-row-reverse max-w-1200">
        <div class="px-4">
          <div class="rounded overflow-hidden mb-6">
            <OImage v-bind="headerImage" img-class="rounded" />
          </div>
        </div>
        <div class="px-4 md:pr-0">
          <h1 class="o-heading-1 mb-1" v-text="$t('Loyalty.LoyaltyProgram.NewLoyaltyPage.Title', variables)" />
          <p class="o-body" v-text="$t('Loyalty.LoyaltyProgram.NewLoyaltyPage.Description', variables)" />
          <DontWorryUSPS class="my-3" :usps="usps" />
          <NewButton tag="nuxt-link" :to="isAuthenticated ? '/refer-a-friend' : '/signup'" icon-right="new-chevron-right" color="blue">
            {{ isAuthenticated ? $t('Loyalty.LoyaltyProgram.NewLoyaltyPage.EarnMoreCredits', variables) : $t('HomePage.Signup', variables) }}
          </NewButton>
        </div>
      </div>
    </div>
    <div class="max-w-946 md:py-10">
      <LoyaltyReferralLinkSharer
        :country="country"
        :link="isAuthenticated ? getReferralLink(spaazaUser.referralCode, user.firstName) : null"
        @signup="$router.push($i18n.localePath({ name: 'signup' }))"
        @click="shareLink({
          medium: $event.medium.Icon,
          link: getReferralLink(spaazaUser.referralCode, user.firstName),
          code: spaazaUser.referralCode,
          userId: (isAuthenticated && user) ? user.user._id : undefined
        })"
      />
    </div>
    <div class="pt-10 max-w-948 md:pb-10">
      <div class="px-4 md:text-center">
        <p class="o-heading-2 mb-1" v-text="$t('Loyalty.LoyaltyProgram.NewLoyaltyPage.WhatAreYouSavingFor')" />
        <p class="o-body" v-text="$t('Loyalty.LoyaltyProgram.NewLoyaltyPage.SaveForExclusiveItems')" />
      </div>
      <WebshopProductSlider class="mt-6" :products="products" />
    </div>
    <div class="mt-6 px-4 max-w-948 pb-10">
      <ViewWebshopBlock />
    </div>
  </div>
</template>

<script>
import { GET_SPAAZA_USER } from '@/utils/store/actionTypes'
import { IS_AUTHENTICATED } from '@/utils/store/getterTypes'
import { getImagePaths, getEuros } from '@/utils/general'
import { mapState, mapActions, mapGetters } from 'vuex'
import linkSharerMixin from '@/mixins/linksharer'
import { WomenCountries } from '@/enums/general'
import NewButton from '@/components/NewButton'
import OImage from '@/components/OImage'
import ViewWebshopBlock from '@/components/ViewWebshopBlock'
import LoyaltyReferralLinkSharer from '@/components/LoyaltyReferralLinkSharer'
import DontWorryUSPS from '@/components/Women/Signup/DontWorryUSPS'
import WebshopProductSlider from '@/components/WebshopProductSlider'

export default {
  name: 'Loyalty',
  layout: 'homepage',
  components: { NewButton, DontWorryUSPS, OImage, ViewWebshopBlock, LoyaltyReferralLinkSharer, WebshopProductSlider },
  mixins: [linkSharerMixin],
  async fetch () {
    try {
      if (!this.spaazaUser.referralCode) {
        await this.getSpaazaUser()
      }
    } catch (error) {
      this.$ota.general.logError(error)
    }
  },
  computed: {
    isWomenCountry () {
      return WomenCountries.includes(this.country)
    },
    headerImage () {
      return getImagePaths({
        path: `/images/frontend/loyalty/header-${this.isWomenCountry ? 'mix' : 'men'}`,
        s3: true
      })
    },
    usps () {
      const path = 'Loyalty.LoyaltyProgram.NewLoyaltyPage.USPS'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => this.$t(`${path}[${i}]`, { ...this.variables }))
    },
    products () {
      const path = 'Loyalty.LoyaltyProgram.NewLoyaltyPage.MenProducts'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      const products = items.map((_, i) => ({
        image: getImagePaths({
          path: this.$t(`${path}[${i}].Url`),
          s3: true
        }),
        title: this.$t(`${path}[${i}].Title`, { ...this.variables }),
        description: this.$t(`${path}[${i}].Description`, { ...this.variables })
      }))

      if (this.isWomenCountry) {
        return [products[0], this.womenProducts[0], products[2], this.womenProducts[1]]
      }

      return products
    },
    womenProducts () {
      const path = 'Loyalty.LoyaltyProgram.NewLoyaltyPage.WomenProducts'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => ({
        image: getImagePaths({
          path: this.$t(`${path}[${i}].Url`),
          s3: true
        }),
        title: this.$t(`${path}[${i}].Title`, { ...this.variables }),
        description: this.$t(`${path}[${i}].Description`, { ...this.variables })
      }))
    },
    variables () {
      return {
        loyaltyAmount: getEuros(this.country, 10)
      }
    },
    ...mapState('app', {
      country: state => state.country
    }),
    ...mapState('spaaza', {
      spaazaUser: state => state
    }),
    ...mapState('auth', {
      user: state => state.user
    }),
    ...mapGetters('auth', {
      isAuthenticated: IS_AUTHENTICATED
    })
  },
  methods: {
    ...mapActions('spaaza', {
      getSpaazaUser: GET_SPAAZA_USER
    })
  }
}
</script>

<style lang="postcss" scoped>
.loyalty {
  .dont-worry-usps {
    @apply bg-transparent p-0 text-neutral-800 !important;
  }

  .max-w-1200 {
    @apply mx-auto;
    max-width: 119.2rem;
  }

  .max-w-946 {
    @apply mx-auto;
    max-width: 94.6rem;
  }

  h1 {
    line-height: normal;
  }

  >>> .max-w-51rem {
    max-width: unset;
  }

  .max-w-948 {
    @apply mx-auto;
    max-width: 94.8rem;
  }
}

@screen md {
  .loyalty {
    &-header {
      .oimage >>> img {
        max-width: unset;
      }
    }
  }
}
</style>
